/* script */

import { unzip }           from 'fflate';
import { toGeoJSON }       from 'utils/toGeoJSON';

import {
  GEOMETRY_FIELDS,
  DOTS_PER_INCH,
}                          from 'g3w-constants';
import ApplicationState    from 'g3w-state';
import GUI                 from 'g3w-app';
import { colorFromHsv }    from 'utils/colorFromHsv';
import { colorToHsv }      from 'utils/colorToHsv';
import { getUniqueDomId }  from 'utils/getUniqueDomId';
import { XHR }             from 'utils/XHR';

  
/**
 * ORIGINAL SOURCE: https://gist.github.com/luishdez/644215
 * 
 * Parse a delimited string into an array of arrays.
 */
function _CSVToArray(text, separator = ',') {
  // Create a regular expression to parse the CSV values.
  const pattern = new RegExp(
    (
      '(' + separator + '|\r?\n|\r|^)' + // Delimiters.
      '(?:"([^"]*(?:""[^"]*)*)"|' +      // Quoted fields.
      '([^"' + separator + "\r\n]*))"    // Standard fields.
    ),
    "gi"
  );

  const data = [[]];
  let matches = null;

  while (matches = pattern.exec(text)) {
    // exclude new line value
    if (matches[0] === '\r\n') {
      break;
    }
    // add an empty row to our data array
    if (matches[1].length && (matches[1] !== separator)
    ) {
      data.push([]);
    }
    // captured value (quoted or unquoted).
    data.at(-1).push(matches[2] ? matches[2].replace(new RegExp('""', 'g'), '"') : matches[3]);
  }

  // parsed data
  return data;
}

const __vue_script__ = {

  /** @since 3.11.0 */
  name: 'modal-addlayer',

  data() {

    const layerColor = {
      hex:  '#194d33',
      rgba: { r: 25, g: 77, b: 51, a: 1 },
      a:    1,
    };

    return {
      is_localhost:   'localhost' === window.location.hostname,
      pid:             ApplicationState.project.getId(),
      layer_type:      undefined,
      file_type:       null,
      layer_name:      null,
      layer_crs:       ApplicationState.project.getProjection().getCode(),
      ...colorToHsv(layerColor),
      alpha:            layerColor.a,
      layer_color:      layerColor,
      wms_config:       null,
      wms_urls:         [],   // array of object {id, url}
      wms_projection:   null, // choose epsg project
      wms_styles:       [],   // selected layers styles
      wms_layers:       [],   // selected layers
      wms_layers_filter: '',  // search filter (wms layers)
      wms_visible:      true,
      wms_opacity:      1,
      url:              null,
      id:               null,
      olLayer:          null,
      map_crs:          ApplicationState.project.getProjection().getCode(),
      layer_data:       null,
      position:         'top', // layer position on map
      persistent:       false,
      loading:          false, // loading reactive status
      fields:           [],
      field:            null,
      csv_x:            null,
      csv_y:            null,
      csv_wkt:          null, //@since 3.11.0
      csv_separator:    ',',
      csv_loading:      false,
      name:             undefined,  // name of saved layer
      title:            null,       // title of layer
      layers:           [],         // Array of layers
      projections:      [],         // projections
      error_message:    '',
      parse_errors:     [],
      tms_url:         '',
      tms_name:        '',
      tms_projection:  'EPSG:3857',
      tms_visible:     true,
      tms_opacity:     1,
    }
  },

  computed: {

    /** Current selection in the color object format used by layer styles. */
    color() {
      return colorFromHsv(this.hue, this.saturation, this.brightness, this.alpha);
    },

    /** Fully saturated hue used as the plane's background. */
    hueColor() {
      return colorFromHsv(this.hue, 1, 1).hex;
    },

    /**
     * @since 4.1.0 
     */
    can_add_layer() {
      if ('wms' === this.layer_type) { 
        return this.wms_layers.length
      } 
      if ('tms' === this.layer_type) { 
        return this.tms_name && this.tms_url && !GUI.getLocalStorage('externallayers').data.find(layer => 'tms' === layer.type && this.tms_url === layer.url && this.pid === layer.pid);
      } 
      return this.layer_data;
    },

    feature_count() {
      return this.olLayer?.getSource().getFeatures().length || 0;
    },

    wms_filtered_layers() {
      const filter = (this.wms_layers_filter || '').toLowerCase().trim();
      // preserve original index via __idx so wms_styles (which store original indexes) keep working
      let mapped = (this.layers || []).map((l, idx) => ({ ...l, __idx: String(idx) }));

      // apply filter if present, otherwise show all
      if (filter) {
        mapped = mapped.filter(l => ((l.title || '') + (l.name || '') + (l.abstract || '')).toLowerCase().includes(filter));
      }

      return mapped;
    },

    wms_all_selected() {
      const vis = (this.wms_filtered_layers || []).map(l => String(l.__idx));
      if (!vis.length) return false;
      return vis.every(id => (this.wms_styles || []).includes(id));
    },

  },

  watch: {

    // Rebuild the HSV controls when the layer color is initialized or reset.
    layer_color: {
      deep: true,
      handler(value) {
        this.syncColorPicker(value);
      },
    },

    /**
     * Handle selected layers change  
     */
     wms_styles(wms_styles = []) {
      const config      = this.wms_config || {};
      const layers      = (config.layers || []).filter((l,i) => wms_styles.includes(i.toString()));
      const last        = (config.layers || []).findLastIndex(l => l == layers.at(-1));
      const projections = (config.layers || []).map(({ crss }) => crss.map(crs => `EPSG:${crs.epsg}`).sort())[last];

      if (0 === layers.length) {        // Reset epsg and projections to initial values
        this.wms_projection = null;
        this.projections    = [];
      } else if (1 === layers.length) { // take first layer selected supported crss
        this.wms_projection = projections[0];
        this.projections    = projections;
      } else {                          // get projections by name
        this.projections = this.projections.filter(p => projections.includes(p));
      }

      if (layers.length) {
        let i = 0;
        const styles = ` (${layers.map(l => l.title).join(' + ')})`;
        let suffix = styles;
        while(GUI.getLayerByName(config.title + suffix)) {
          suffix = ` ${styles} (${ ++i })`;
        }
        this.name = config.title + suffix;
      }

      this.wms_layers = layers;
    },

    /**
     * @returns { Promise<void> }
     */
    async wms_projection() {
      await this.$nextTick();
      const config      = this.wms_config || {};
      const projections = (config.layers || []).map(({ crss }) => crss.map(crs => `EPSG:${crs.epsg}`).sort());
      // Get layers that have current selected epsg projection
      this.layers = (null === this.wms_projection)
        ? config.layers
        : config.layers.filter((l,i) => projections[i].includes(this.wms_projection))
    },

    async layer_type(type, oldtype) {
      if (type && oldtype) {
        this.layer_type = undefined;
        await this.$nextTick();
        this.layer_type = type;
      }
      if ('file' === oldtype) {
        this.unloadFile();
      }
      if ('wms' === oldtype) {
        this.unloadWMS();
      }
    },

    url() {
      if (this.url && !this.wms_config && this.wms_urls.some(l => this.url == l.url)) {
        this.id = this.wms_urls.find(l => this.url == l.url).id;
      } else if (!this.url) {
        this.id = '';
      }
    },

  },

  methods: {

    /** Synchronize the HSV controls when the layer color is initialized or reset. */
    syncColorPicker(value) {
      Object.assign(this, colorToHsv(value), { alpha: value.a });
    },

    /** Store the current selection as the layer color. */
    emitColor() {
      this.layer_color = this.color;
    },

    onColorPlanePointerDown(event) {
      event.currentTarget.setPointerCapture(event.pointerId);
      this.updateColorPlane(event);
    },

    /** Continue updating the plane while a pointer button is held. */
    onColorPlanePointerMove(event) {
      if (event.buttons) {
        this.updateColorPlane(event);
      }
    },

    /** Map pointer coordinates to saturation and brightness, clamped to the plane. */
    updateColorPlane(event) {
      const bounds = event.currentTarget.getBoundingClientRect();
      this.saturation = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
      this.brightness = 1 - Math.min(1, Math.max(0, (event.clientY - bounds.top) / bounds.height));
      this.emitColor();
    },

    /** Adjust saturation or brightness by two percent with the arrow keys. */
    onColorPlaneKeydown(event) {
      const movement = {
        ArrowLeft:  [-0.02, 0],
        ArrowRight: [ 0.02, 0],
        ArrowUp:    [0,  0.02],
        ArrowDown:  [0, -0.02],
      }[event.key];

      if (!movement) {
        return;
      }

      event.preventDefault();
      this.saturation = Math.min(1, Math.max(0, this.saturation + movement[0]));
      this.brightness = Math.min(1, Math.max(0, this.brightness + movement[1]));
      this.emitColor();
    },

    /** Apply the hue slider's degree value while preserving saturation and brightness. */
    onColorHueInput(event) {
      this.hue = Number(event.target.value);
      this.emitColor();
    },

    /** Apply the opacity slider while preserving the selected HSV color. */
    onColorOpacityInput(event) {
      this.alpha = Number(event.target.value);
      this.emitColor();
    },

    onToggleSelectAllWMS(e) {
      const checked    = e.target.checked;
      const visibleIds = (this.wms_filtered_layers || []).map(l => String(l.__idx));
      if (checked) {
        // add visible ids preserving order and avoiding duplicates
        const current = [...(this.wms_styles || [])];
        visibleIds.forEach(id => { if (!current.includes(id)) current.push(id); });
        this.wms_styles = current;
      } else {
        // remove visible ids from current selection
        this.wms_styles = (this.wms_styles || []).filter(id => !visibleIds.includes(id));
      }
    },

    async parseFile() {
      const input = this.$refs.input_file;

      // skip invalid formats
      if (!input.accept.split(',').includes(`.${input.files[0].name.split('.').at(-1).toLowerCase()}`)) {
        this.error_message = 'Not supported format';
        return;
      }

      // skip invalid names
      if (GUI.getLayerByName(input.files[0].name)) {
        this.error_message = 'Layer with same name already added';
        return;
      }

      try {

        this.error_message = '';
        this.parse_errors  = [];
        this.layer_name    = input.files[0].name;
        this.file_type     = input.files[0].name.split('.').at(-1).toLowerCase();
        this.layer_data    = null;

        let features = [];
        let data;

        (this.fields || []).splice(0); // reset fields

        // KMZ file
        if ('kmz' === this.file_type) {
          const kmzData = new Uint8Array(await input.files[0].arrayBuffer());
          const archive = await new Promise((resolve, reject) => {
            unzip(kmzData, (error, files) => {
              if (error) {
                reject(error);
                return;
              }
              resolve(files);
            });
          });
          const kmlPath = Object.keys(archive).filter(path => /\.kml$/i.test(path)).at(-1); // get last kml file within folder
          if (!kmlPath) {
            throw new Error('KMZ archive does not contain a KML file');
          }
          data = new TextDecoder().decode(archive[kmlPath]);
        }

        // SHAPE FILE
        if ('zip' === this.file_type) {
          const out = {}; // un-zip folder data
          const zipData = new Uint8Array(await input.files[0].arrayBuffer());
          const files = await new Promise((resolve, reject) => {
            unzip(zipData, (error, unzipped) => {
              if (error) {
                reject(error);
                return;
              }
              resolve(unzipped);
            });
          });
          const decoder = new TextDecoder();
          for (const [path, bytes] of Object.entries(files)) {
            if (!/.+\.(shp|dbf|json|prj|cpg)$/i.test(path)) {
              continue;
            }
            const ext = path.split('.').at(-1).toLowerCase();
            if ('shp' === ext || 'dbf' === ext) {
              out[ext] = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
              continue;
            }
            out[ext] = decoder.decode(bytes);
          }
          data = JSON.stringify(await toGeoJSON(out)); // convert to wsg84 (geojson)
        }

        // CSV file
        if ('csv' === this.file_type) {
          this.csv_loading = true;

          data         = _CSVToArray(await input.files[0].text(), this.csv_separator);
          const X      = ['x', 'lng', 'longitude', 'longitudine'];
          const Y      = ['y', 'lat', 'latitude', 'latitudine'];
          this.fields  = data.shift();
          const wkt    = this.fields.findIndex(f => 'wkt' === f.toLowerCase());
          const x      = this.fields.findIndex(f => X.includes(f.toLowerCase()));
          const y      = this.fields.findIndex(f => Y.includes(f.toLowerCase()));
          this.csv_wkt = this.csv_wkt || this.fields[wkt];                               // auto suggest "wkt" field
          this.csv_x   = this.csv_wkt || this.csv_x || this.fields[x] || this.fields[0]; // auto suggest "csv_x" field
          this.csv_y   = this.csv_wkt || this.csv_y || this.fields[y] || this.fields[1]; // auto suggest "csv_y" field

          data.forEach((row, i) => {
            const X = Number(row[x]);
            const Y = Number(row[y]);
            // check if coordinates are right
            if (!this.csv_wkt && (Number.isNaN(X) || Number.isNaN(Y))) {
              return this.parse_errors.push({ row: i + 1, value: data[i] });
            }
            try {
              const feat = new ol.Feature({
                geometry: (new ol.format.WKT()).readGeometry(this.csv_wkt ? row[wkt] : `POINT (${X} ${Y})`, {
                  dataProjection:    this.layer_crs,
                  featureProjection: GUI.getEpsg()
                }),
                ...(row.reduce((props, value, i) => { props[this.fields[i]] = value; return props; }, {}))
              });
              feat.setId(i);
              features.push(feat);
            } catch(e) {
              console.warn(e);
            }
          });

          this.csv_loading = false;
        }

        // other files
        if (!data) {
          data = await input.files[0].text() || {};
        }

        this.layer_crs  = ['kml','kmz'].includes(this.file_type) ? 'EPSG:4326' : this.layer_crs;

        // register EPSG
        await ApplicationState.projections.set(this.layer_crs);

        this.layer_data = data;

        // parse features
        if ('csv' !== this.file_type) {
          features = ({
            'gpx'    : new ol.format.GPX(),
            'gml'    : new ol.format.WMSGetFeatureInfo(),
            'geojson': new ol.format.GeoJSON(),
            'zip'    : new ol.format.GeoJSON(),
            'kml'    : new ol.format.KML({ extractStyles: false }),
            'kmz'    : new ol.format.KML({ extractStyles: false }),
            // 'csv'    : new ol.format.WKT(),
          })[this.file_type].readFeatures(data, {
            dataProjection:    this.layer_crs,
            featureProjection: GUI.getEpsg() || this.layer_crs,
          });
        }

        // @since 3.11.0 toGeoJSON always creates features in 4326 coordinates
        if ('zip' === this.file_type && this.layer_crs !== 'EPSG:4326') {
          features.forEach(f => f.getGeometry().transform('EPSG:4326', this.layer_crs));
        }

        // ignore kml property [`<styleUrl>`](https://developers.google.com/kml/documentation/kmlreference)
        if (['kml', 'kmz'].includes(this.file_type)) {
          features.forEach(f => f.unset('styleUrl'));
        }

        if (features.length > 0) {
          this.olLayer = new ol.layer.Vector({
            source: new ol.source.Vector({ features }),
            name:   this.layer_name,
            id:     getUniqueDomId(),
          });
          this.fields = 'csv' === this.file_type ? this.fields : Object.keys(features[0].getProperties()).filter(prop => GEOMETRY_FIELDS.indexOf(prop) < 0);
        }

      } catch(e) {
        console.warn(e);
        this.error_message = `${e}`;
      }
    },

    async addLayer() {
      this.loading = true;
      // check if External Local layers already added (by name)
      const data = GUI.getLocalStorage('externallayers');
      if ('wms' === this.layer_type) {
        const name = (this.name || `wms_${getUniqueDomId()}`).trim();

        try {
      
          const found = this.wms_config && data.data.some(layer =>
            'wms' === layer.type 
            && this.url === layer.url
            && layer.layers.length === this.wms_layers.length
            && this.wms_layers.every(l => layer.layers.includes(l.name))
            && this.pid === layer.pid
          );

          if (found) {
            await this.fetchWMS(this.url);
          }

          const config = {
            type:     'wms',
            pid:      this.pid,
            url:      this.url,
            name,
            layers:   this.wms_layers.map(l => l.name),
            epsg:     this.wms_projection,
            position: this.position,
            visible:  this.wms_visible,
            opacity:  +this.wms_opacity,
          };

          data.data = data.data.concat(config);

          GUI.setLocalStorage('externallayers', data);

          try {
            await this._addExternalWMSLayer(config);
          } catch(e) {
            console.warn(e);
            GUI.removeExternalLayer(name);
            setTimeout(() => { GUI.showUserMessage({ type: 'warning', message: 'WMS Layer not added. Please check all wms parameter or url' }) });
          }
        } catch(e) {
          console.warn(e);
        }
        if (this.wms_config) {
          this.close();
        }
      }

      if ('tms' === this.layer_type) {
        try {
          const config = {
            type:     'tms',
            pid:      this.pid,
            url:      this.tms_url,
            name:     this.tms_name,
            position: this.position,
            opacity:  +this.tms_opacity,
            visible:  this.tms_visible,
            crs:      this.tms_projection,
          };

          data.data = data.data.concat(config);

          GUI.setLocalStorage('externallayers', data);

          try {
            await this._addExternalTMSLayer(config);
          } catch(e) {
            console.warn(e);
            GUI.removeExternalLayer(this.tms_name);
            setTimeout(() => { GUI.showUserMessage({ type: 'warning', message: 'TMS Layer not added. Please check all wms parameter or url' }) });
          }
        } catch(e) {
          console.warn(e);
        }
        this.close();
      }

      if ('file' === this.layer_type) {
        try {
          await GUI.addExternalLayer(this.olLayer, {
            crs:        this.layer_crs,
            position:   this.position,
            color:      this.layer_color,
            field:      this.field,
            persistent: !!this.persistent,
            type:       this.file_type,
          });
          this.close();
        } catch(e) {
          console.warn(e);
          this.error_message = `${e}`;
        }
      }
      this.loading = false;
    },

    unloadFile() {
      this.error_message           = '';
      this.parse_errors            = [];
      this.loading                 = false;
      this.layer_name              = null;
      this.file_type               = null;
      this.layer_crs               = GUI.getCrs();
      this.layer_color             = { hex: '#194d33', rgba: { r: 25, g: 77, b: 51, a: 1 }, a: 1 };
      this.layer_data              = null;
      this.olLayer                 = null;
      this.fields                  = [];
      this.field                   = null;
      this.csv_x                   = null;
      this.csv_y                   = null;
      this.csv_wkt                 = null;
      //reset input file value to null to accept new file
      if (this.$refs.input_file) {
        this.$refs.input_file.value = null;
      }
    },

    /**
     * @since 4.1.0 
     * Reset tms fields
     */
    unloadTMS() {
      this.tms_url        = '';
      this.tms_name       = '';
      this.tms_projection = 'EPSG:3857';
      this.tms_visible    = true;
      this.tms_opacity    = 1;
    },

    unloadWMS() {
      let url              = this.url;
      this.url             = '';
      this.error_message   = '';
      this.wms_config      = null;
      this.wms_layers      = [];
      this.wms_opacity     = 1;
      this.wms_visible     = true;
      this.wms_styles      = [];
      this.name            = null;
      this.loading         = false;
      // HOTFIX: for wms name not showed when unloading server connection
      setTimeout(() => this.url = url);
    },

    /**
     * @returns { Promise<void> }
     */
    async addWmsURL() {
      this.loading = true;
      const wms    = { url: this.url, id: this.id, type: 'wms', pid: this.pid };
      const found  = this.wms_urls.find(l => this.url === l.url);
      try {
        await this.fetchWMS(this.url);
        if (!found) {
          const data = GUI.getLocalStorage('externallayers');
          this.wms_urls.push(wms);
          data.urls = (data.urls || []).concat(wms);
          GUI.setLocalStorage('externallayers', data);
        }
      } catch(e) {
        console.warn(e);
      }
      this.loading = false;
    },

    /**
     * Delete url from local storage
     * 
     * @param id
     */
    deleteWmsUrl(id) {
      this.wms_urls = this.wms_urls.filter(l => id !== l.id);
      const data    = GUI.getLocalStorage('externallayers');
      data.urls     = (data.urls || []).filter(u => u.pid !== this.pid || u.id !== id);
      GUI.setLocalStorage('externallayers', data);
    },

     /**
     * Add external TMS layer to map
     * 
     * @param { Object } tms
     * @param { string } tms.url
     * @param { string } tms.name
     * @param tms.epsg
     * @param tms.position
     * @param tms.opacity
     * @param tms.visible
     *
     * @returns {Promise<unknown>}
     */
    async _addExternalTMSLayer({
      url,
      name,
      epsg     = GUI.getEpsg(),
      position = 'top',
      opacity,
      visible  = true
    } = {}) {
      return new Promise((res, rej) => {
        name = name || getUniqueDomId();

        const olLayer = new ol.layer.Tile({
          source:  new ol.source.XYZ({
              url,
              projection:  ol.proj.get(epsg)?.getCode?.() ?? null,
              crossOrigin: 'anonymous',
            }),
          opacity,
          visible,
          id:      name,
          name:    name,
        })

        olLayer.getSource().once('tileloadend', res);
        olLayer.getSource().once('tileloaderror', rej);         

        GUI.addExternalLayer(olLayer, { position, opacity, visible, type: 'tms' });

        // HOTFIX: for hidden wms layers
        if (!this.tms_visible || !this.tms_opacity) {
          setTimeout(res, 1000);
        }
      });
 
    },

    /**
     * Add external WMS layer to map
     * 
     * @param { Object } wms
     * @param { string } wms.url
     * @param { string } wms.name
     * @param wms.epsg
     * @param wms.position
     * @param wms.opacity
     * @param wms.visible
     * @param wms.layers
     *
     * @returns {Promise<unknown>}
     */
    _addExternalWMSLayer({
      url,
      layers,
      name,
      epsg     = GUI.getEpsg(),
      position = 'top',
      opacity,
      visible  = true
    } = {}) {
      return new Promise((res, rej) => {
        name = name || getUniqueDomId();

        const olLayer = new ol.layer.Image({
          name,
          id:            name,
          opacity:       1.0,
          source:        new ol.source.ImageWMS({
            ratio:      1,
            url,
            projection: ol.proj.get(epsg)?.getCode?.() ?? null,
            // crossOrigin: 'anonymous',
            params:     Object.fromEntries(Object.entries({
              DPI:         DOTS_PER_INCH,
              TRANSPARENT: true,
              LAYERS:      layers ?? '',
              VERSION:     '1.3.0',
              SLD_VERSION: '1.1.0',
            })),
          })
        });

        olLayer.getSource().once('imageloadend', res);
        olLayer.getSource().once('imageloaderror', rej);

        GUI.addExternalLayer(olLayer, { position, opacity, visible, type: 'wms' });

        // HOTFIX: for hidden wms layers
        if (!this.wms_visible || !this.wms_opacity) {
          setTimeout(res, 1000);
        }
      });
    },

    /**
     * ORIGINAL SOURCE: src/app/gui/wms/vue/panel/wmslayerspanel.js@3.8.15
     * 
     * show add wms layers to wms panel
     * 
     * @param config
     * 
     * @returns { WmsLayersPanel }
     */
    async fetchWMS(url) {
      this.loading = true;
      try {
        const config = await XHR.post({
          url:         `${window.initConfig.interfaceowsurl}`,
          contentType: 'application/json',
          data:        JSON.stringify({ url: url || this.url, service: "wms" })
        });

        // skip on invalid response
        if (!config.result) {
          throw 'invalid response';
        }

        config.wmsurl = url || this.url;

        /** URL of wms */
        if (config.methods && config.methods.GetMap) {
          this.url = (config.methods.GetMap.urls || []).find(u => 'Get' === u.type).url;
        } else {
          this.url = config.wmsurl;
        }

        /** Title of wms */
        this.title = config.title;

        let i = 0;
        let suffix = '';
        while(GUI.getLayerByName(config.title + suffix)) {
          suffix = ` (${ ++i })`;
        }
        this.name  = config.title + suffix;

        // register projections
        config.layers.forEach(({ crss }) => crss.forEach(crs => ApplicationState.projections.set(crs.epsg)));

        /** Layers of wms */
        this.layers = config.layers;

        this.wms_config    = config;
        this.error_message = null;
      } catch(e) {
        console.warn(e);
        this.error_message = e;
      }
      this.loading = false;
    },

    /**
     * @since 4.1.0
     */
    async onBeforetoggle(e) {
      if ('open' === e.newState && window.innerWidth < 767) {
        GUI.hideSidebar();
      }
      if ('closed' === e.newState) {
        this.close(false);
      }
    },

    /**
     * @since 4.1.0
     */
    close(hide = true) {
      if (hide) {
        $('#modal-addlayer').modal('hide');
      }
      this.layer_type = undefined;
      this.unloadFile();
      this.unloadWMS();
      this.unloadTMS();
    },

  },

  async mounted() {
    document.body.appendChild(this.$el);

    await GUI.isMapReady();

    let data = GUI.getLocalStorage('externallayers');

    if (!data.data) {
      data = {
        urls:  [], // unique url for wms
        data:  [], // object contains url as a key and array of layers bind to url
      };
      GUI.setLocalStorage('externallayers', data);
    }

    // reload layers from local storage
    data.data.forEach(layer => { 
      if (this.pid !== layer.pid) {
        return;
      }
      if ('wms' === layer.type) {
        this._addExternalWMSLayer(layer);
      }
      if ('tms' === layer.type) {
        this._addExternalTMSLayer(layer);
      }
    });

    this.wms_urls = data.urls.filter(u => u.pid === this.pid);
  },

};


/* template */
var render = function render(){var _vm=this,_c=_vm._self._c;return _c('dialog',{style:({ width: _vm.wms_config ? 'min(85vw, 1400px)' : 'min(85vw, 600px)' }),attrs:{"id":"modal-addlayer","aria-label":_vm.$t('Add Layer')},on:{"beforetoggle":_vm.onBeforetoggle}},[_c('form',{staticStyle:{"padding":"15px"},attrs:{"method":"dialog"}},[_c('button',{staticStyle:{"border":"none","line-height":"1","font-weight":"700","font-size":"25px","background":"none","position":"absolute","inset":"15px 15px auto auto","width":"40px","height":"40px"},attrs:{"value":"cancel","formnovalidate":""}},[_vm._v("×")]),_vm._v(" "),_c('h4',{directives:[{name:"t",rawName:"v-t",value:('Add Layer'),expression:"'Add Layer'"}],staticStyle:{"font-weight":"bold"}}),_vm._v(" "),_c('hr'),_vm._v(" "),_c('div',[_c('div',{staticClass:"form-group"},[_c('label',{directives:[{name:"t",rawName:"v-t",value:('Layer type'),expression:"'Layer type'"}]}),_vm._v(" "),_c('select',{directives:[{name:"model",rawName:"v-model",value:(_vm.layer_type),expression:"layer_type"}],staticClass:"form-control",attrs:{"id":"add-layer-type"},on:{"change":function($event){var $$selectedVal = Array.prototype.filter.call($event.target.options,function(o){return o.selected}).map(function(o){var val = "_value" in o ? o._value : o.value;return val}); _vm.layer_type=$event.target.multiple ? $$selectedVal : $$selectedVal[0]}}},[_c('option',{directives:[{name:"t",rawName:"v-t",value:('Choose type'),expression:"'Choose type'"}],attrs:{"disabled":""},domProps:{"value":undefined}}),_vm._v(" "),_c('option',{directives:[{name:"t",rawName:"v-t",value:('WMS (URL)'),expression:"'WMS (URL)'"}],attrs:{"value":"wms"}}),_vm._v(" "),_c('option',{directives:[{name:"t",rawName:"v-t",value:('TMS (URL)'),expression:"'TMS (URL)'"}],attrs:{"value":"tms"}}),_vm._v(" "),_c('option',{directives:[{name:"t",rawName:"v-t",value:('Local file'),expression:"'Local file'"}],attrs:{"value":"file"}})])]),_vm._v(" "),_c('hr'),_vm._v(" "),_c('div',{directives:[{name:"show",rawName:"v-show",value:(_vm.loading),expression:"loading"}],staticClass:"bar-loader"}),_vm._v(" "),('wms' === _vm.layer_type)?_c('div',{staticClass:"form-group"},[_c('fieldset',{staticClass:"form-group",staticStyle:{"width":"100%"},attrs:{"disabled":_vm.wms_config}},[_c('a',{staticStyle:{"float":"right"},attrs:{"href":`https://g3w-suite.readthedocs.io/en/v3.9.x/g3wsuite_client.html#wms`,"target":"_blank","data-i18n-title":"Docs","data-placement":"bottom"}},[_c('i',{staticClass:"fa fa-external-link-alt",attrs:{"aria-hidden":"true"}})]),_vm._v(" "),_c('label',{attrs:{"for":"add_wms_url"}},[_vm._v("URL")]),_vm._v(" "),_c('input',{directives:[{name:"model",rawName:"v-model.trim",value:(_vm.url),expression:"url",modifiers:{"trim":true}}],staticClass:"form-control",attrs:{"id":"add_wms_url","placeholder":"http://example.org/?&service=WMS&request=GetCapabilities","type":"url","list":"wms_urls","required":""},domProps:{"value":(_vm.url)},on:{"input":function($event){if($event.target.composing)return;_vm.url=$event.target.value.trim()},"blur":function($event){return _vm.$forceUpdate()}}}),_vm._v(" "),(!_vm.wms_config)?_c('small',{directives:[{name:"t",rawName:"v-t",value:('Search through saved connections or add a new server'),expression:"'Search through saved connections or add a new server'"}]}):_vm._e(),_vm._v(" "),_c('datalist',{attrs:{"id":"wms_urls"}},_vm._l((_vm.wms_urls),function(wms){return _c('option',{key:wms.id,domProps:{"value":wms.url}},[_vm._v(_vm._s(wms.id))])}),0)]),_vm._v(" "),(_vm.url && !_vm.wms_config && !_vm.loading)?_c('fieldset',{staticClass:"form-group",attrs:{"disabled":_vm.wms_config || _vm.wms_urls.some(l => _vm.url == l.url)}},[_c('label',{attrs:{"for":"add_wms_name","title":"required"}},[_c('span',{directives:[{name:"t",rawName:"v-t",value:('Name'),expression:"'Name'"}]}),_vm._v(" "),_c('i',{staticStyle:{"font-family":"Monospace","color":"var(--skin-color)"}},[_vm._v("*")])]),_vm._v(" "),_c('input',{directives:[{name:"model",rawName:"v-model.trim",value:(_vm.id),expression:"id",modifiers:{"trim":true}}],staticClass:"form-control",attrs:{"id":"add_wms_name","type":"text","required":""},domProps:{"value":(_vm.id)},on:{"input":function($event){if($event.target.composing)return;_vm.id=$event.target.value.trim()},"blur":function($event){return _vm.$forceUpdate()}}}),_vm._v(" "),(null !== _vm.id && _vm.wms_urls.some(l => _vm.id === l.id) && _vm.wms_urls.every(l => _vm.url !== l.url))?_c('p',{staticStyle:{"color":"red","margin":"10px 0"}},[_vm._v("\n            ⚠️ "),_c('b',{directives:[{name:"t",rawName:"v-t",value:('A WMS connection with this name already exists'),expression:"'A WMS connection with this name already exists'"}]})]):_vm._e()]):_vm._e(),_vm._v(" "),(!_vm.wms_config)?_c('button',{staticClass:"btn btn-block btn-success",attrs:{"disabled":!(_vm.id || '').trim() || _vm.wms_urls.some(l => _vm.id === l.id && _vm.url !== l.url) || !(_vm.url || '').trim().match(/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g)},on:{"click":function($event){$event.preventDefault();$event.stopPropagation();return _vm.addWmsURL.apply(null, arguments)}}},[_c('i',{staticClass:"far fa-plus-square",attrs:{"aria-hidden":"true"}}),_vm._v(" "),_c('span',{directives:[{name:"t",rawName:"v-t",value:('Connect'),expression:"'Connect'"}]})]):_vm._e(),_vm._v(" "),(!_vm.wms_config)?_c('div',{staticClass:"form-group"},[_c('hr'),_vm._v(" "),(_vm.wms_urls.length)?_c('p',{directives:[{name:"t",rawName:"v-t",value:('Saved connections:'),expression:"'Saved connections:'"}],staticStyle:{"text-align":"center","font-weight":"bold"}}):_vm._e(),_vm._v(" "),_vm._l((_vm.wms_urls),function(wms){return _c('div',{key:wms.id,staticStyle:{"border-bottom":"1px solid #ccc","padding-bottom":"3px"}},[_c('div',{staticStyle:{"display":"flex","justify-content":"space-between","align-items":"center","padding-top":"3px"}},[_c('b',{staticStyle:{"flex-grow":"1","cursor":"pointer"},attrs:{"title":_vm.$t('Connect')},on:{"click":function($event){return _vm.fetchWMS(wms.url)}}},[_vm._v(_vm._s(wms.id))]),_vm._v(" "),_c('i',{staticClass:"far fa-eye",staticStyle:{"color":"var(--skin-color)","padding":"3px","margin":"2px","font-size":"1.3em","cursor":"pointer"},attrs:{"title":"Connect","data-placement":"top"},on:{"click":function($event){return _vm.fetchWMS(wms.url)}}}),_vm._v(" "),_c('i',{staticClass:"fas fa-trash",staticStyle:{"color":"red","padding":"3px","margin":"2px","font-size":"1.3em","cursor":"pointer"},attrs:{"title":"Remove","data-placement":"top"},on:{"click":function($event){return _vm.deleteWmsUrl(wms.id)}}})]),_vm._v(" "),_c('small',{staticStyle:{"cursor":"pointer"},attrs:{"title":_vm.$t('Connect')},on:{"click":function($event){return _vm.fetchWMS(wms.url)}}},[_vm._v(_vm._s(wms.url))])])})],2):_vm._e(),_vm._v(" "),(_vm.wms_config)?_c('fieldset',{attrs:{"disabled":_vm.loading}},[_c('button',{staticClass:"close",staticStyle:{"float":"right","padding":"5px 10px","margin-top":"15px","outline":"1px solid","color":"red","opacity":"1"},attrs:{"type":"button","title":"Disconnect","data-placement":"left"},on:{"click":_vm.unloadWMS}},[_vm._v("×")]),_vm._v(" "),_c('h3',{staticClass:"skin-color g3w-wms-panel-title"},[_vm._v(_vm._s(_vm.title))]),_vm._v(" "),(_vm.wms_config.abstract)?_c('fieldset',{staticClass:"form-group",staticStyle:{"border":"1px solid #c0c0c0","padding":"4.9px 8.75px 8.75px 10.5px","border-radius":"3px","font-size":"small"}},[_c('legend',{staticStyle:{"width":"15px","height":"15px","border":"none","border-radius":"50%","background-color":"rgb(34, 45, 50)","font-weight":"bold","color":"rgb(255, 255, 255)","font-size":"0.7em","display":"flex","justify-content":"center","margin":"0px -14px","user-select":"none"}},[_vm._v("i")]),_vm._v("\n            "+_vm._s(_vm.wms_config.abstract)+"\n          ")]):_vm._e(),_vm._v(" "),_c('div',{staticClass:"form-group"},[_c('label',{attrs:{"for":"g3w-wms-layers"}},[_vm._v(_vm._s(_vm.$t('Layers')))]),_vm._v(" "),_c('input',{directives:[{name:"model",rawName:"v-model",value:(_vm.wms_layers_filter),expression:"wms_layers_filter"}],staticClass:"form-control",staticStyle:{"margin-bottom":"8px"},attrs:{"id":"g3w-wms-layers","placeholder":_vm.$t('Type to search layers'),"aria-label":_vm.$t('Type to search layers')},domProps:{"value":(_vm.wms_layers_filter)},on:{"input":function($event){if($event.target.composing)return;_vm.wms_layers_filter=$event.target.value}}}),_vm._v(" "),_c('p',[_c('small',[_vm._v(_vm._s(_vm.wms_styles.length)+" "+_vm._s(_vm.$t('layers selected')))])]),_vm._v(" "),(_vm.wms_filtered_layers.length)?_c('table',{staticClass:"table",staticStyle:{"width":"100%","display":"block","max-height":"300px","overflow-y":"scroll","user-select":"none"}},[_c('thead',{staticStyle:{"position":"sticky","top":"0","background-color":"#f4f4f4"}},[_c('tr',[_c('th',{staticStyle:{"width":"40px","text-align":"center"}},[_c('input',{attrs:{"type":"checkbox","aria-label":_vm.$t('Select all layers')},domProps:{"checked":_vm.wms_all_selected},on:{"change":function($event){$event.stopPropagation();return _vm.onToggleSelectAllWMS.apply(null, arguments)}}})]),_vm._v(" "),_c('th',{directives:[{name:"t",rawName:"v-t",value:('Title'),expression:"'Title'"}]}),_vm._v(" "),_c('th',{directives:[{name:"t",rawName:"v-t",value:('Name'),expression:"'Name'"}]}),_vm._v(" "),_c('th',{directives:[{name:"t",rawName:"v-t",value:('Abstract'),expression:"'Abstract'"}]})])]),_vm._v(" "),_c('tbody',_vm._l((_vm.wms_filtered_layers),function(l,i){return _c('tr',{key:l.__idx,style:({ background: _vm.wms_styles.includes(String(l.__idx)) ? 'rgba(25,77,51,0.05)' : 'transparent', cursor: 'pointer' }),on:{"click":function($event){_vm.wms_styles.includes(String(l.__idx)) ? _vm.wms_styles.splice(_vm.wms_styles.indexOf(String(l.__idx)),1) : _vm.wms_styles.push(String(l.__idx))}}},[_c('td',{staticStyle:{"width":"40px","text-align":"center"}},[_c('input',{directives:[{name:"model",rawName:"v-model",value:(_vm.wms_styles),expression:"wms_styles"}],attrs:{"type":"checkbox"},domProps:{"value":String(l.__idx),"checked":Array.isArray(_vm.wms_styles)?_vm._i(_vm.wms_styles,String(l.__idx))>-1:(_vm.wms_styles)},on:{"click":function($event){$event.stopPropagation();},"change":function($event){var $$a=_vm.wms_styles,$$el=$event.target,$$c=$$el.checked?(true):(false);if(Array.isArray($$a)){var $$v=String(l.__idx),$$i=_vm._i($$a,$$v);if($$el.checked){$$i<0&&(_vm.wms_styles=$$a.concat([$$v]))}else{$$i>-1&&(_vm.wms_styles=$$a.slice(0,$$i).concat($$a.slice($$i+1)))}}else{_vm.wms_styles=$$c}}}})]),_vm._v(" "),_c('td',[_c('b',[_vm._v(_vm._s(l.title))])]),_vm._v(" "),_c('td',[_c('small',{staticClass:"text-muted"},[_vm._v(_vm._s(l.name))])]),_vm._v(" "),_c('td',[_c('small',[_vm._v(_vm._s(l.abstract))])])])}),0)]):_vm._e()]),_vm._v(" "),_c('div',{staticClass:"form-group"},[_c('label',{directives:[{name:"t",rawName:"v-t",value:('Projection'),expression:"'Projection'"}],attrs:{"for":"g3w-wms-projections"}}),_vm._v(" "),_c('select',{directives:[{name:"model",rawName:"v-model",value:(_vm.wms_projection),expression:"wms_projection"}],staticClass:"form-control",attrs:{"id":"g3w-wms-projections"},on:{"change":function($event){var $$selectedVal = Array.prototype.filter.call($event.target.options,function(o){return o.selected}).map(function(o){var val = "_value" in o ? o._value : o.value;return val}); _vm.wms_projection=$event.target.multiple ? $$selectedVal : $$selectedVal[0]}}},_vm._l((_vm.projections),function(p){return _c('option',[_vm._v(_vm._s(p))])}),0)]),_vm._v(" "),_c('div',{staticClass:"form-group"},[_c('label',{directives:[{name:"t",rawName:"v-t",value:('layer_position.message'),expression:"'layer_position.message'"}],attrs:{"for":"position-layer"}}),_vm._v(" "),_c('select',{directives:[{name:"model",rawName:"v-model",value:(_vm.position),expression:"position"}],staticClass:"form-control",attrs:{"id":"position-layer"},on:{"change":function($event){var $$selectedVal = Array.prototype.filter.call($event.target.options,function(o){return o.selected}).map(function(o){var val = "_value" in o ? o._value : o.value;return val}); _vm.position=$event.target.multiple ? $$selectedVal : $$selectedVal[0]}}},[_c('option',{directives:[{name:"t",rawName:"v-t",value:('layer_position.top'),expression:"'layer_position.top'"}],domProps:{"value":'top'}}),_vm._v(" "),_c('option',{directives:[{name:"t",rawName:"v-t",value:('layer_position.bottom'),expression:"'layer_position.bottom'"}],domProps:{"value":'bottom'}})])]),_vm._v(" "),_c('select',{directives:[{name:"model",rawName:"v-model",value:(_vm.wms_visible),expression:"wms_visible"}],attrs:{"id":"g3w-wms-visible","hidden":""},on:{"change":function($event){var $$selectedVal = Array.prototype.filter.call($event.target.options,function(o){return o.selected}).map(function(o){var val = "_value" in o ? o._value : o.value;return val}); _vm.wms_visible=$event.target.multiple ? $$selectedVal : $$selectedVal[0]}}},[_c('option',{domProps:{"value":false}}),_vm._v(" "),_c('option',{domProps:{"value":true}})]),_vm._v(" "),_c('div',{staticClass:"form-group"},[_c('label',{directives:[{name:"t",rawName:"v-t",value:('Opacity'),expression:"'Opacity'"}],attrs:{"for":"g3w-wms-opacity"}}),_vm._v(" "),_c('input',{directives:[{name:"model",rawName:"v-model",value:(_vm.wms_opacity),expression:"wms_opacity"}],attrs:{"id":"g3w-wms-opacity","type":"range","min":"0","max":"1","step":"0.01","list":"wms-opacity-markers"},domProps:{"value":(_vm.wms_opacity)},on:{"__r":function($event){_vm.wms_opacity=$event.target.value}}}),_vm._v(" "),_vm._m(0)]),_vm._v(" "),_c('div',{staticClass:"form-group"},[_c('label',{directives:[{name:"t",rawName:"v-t",value:('Name'),expression:"'Name'"}],attrs:{"for":"g3w-wms-layer-name"}}),_vm._v(" "),_c('input',{directives:[{name:"model",rawName:"v-model",value:(_vm.name),expression:"name"}],staticClass:"form-control",attrs:{"id":"g3w-wms-layer-name","type":"text"},domProps:{"value":(_vm.name)},on:{"input":function($event){if($event.target.composing)return;_vm.name=$event.target.value}}})])]):_vm._e()]):_vm._e(),_vm._v(" "),('tms' === _vm.layer_type)?_c('div',{staticClass:"form-group"},[_c('a',{staticStyle:{"float":"right"},attrs:{"href":`https://g3w-suite.readthedocs.io/en/v3.9.x/g3wsuite_client.html#tms`,"target":"_blank","data-i18n-title":"Docs","data-placement":"bottom"}},[_c('i',{staticClass:"fa fa-external-link-alt",attrs:{"aria-hidden":"true"}})]),_vm._v(" "),_c('fieldset',{staticClass:"form-group"},[_c('label',{attrs:{"for":"add_tms_url"}},[_vm._v("URL")]),_vm._v(" "),_c('input',{directives:[{name:"model",rawName:"v-model.trim",value:(_vm.tms_url),expression:"tms_url",modifiers:{"trim":true}}],staticClass:"form-control",attrs:{"id":"add_tms_url","placeholder":"https://{a-c}.tile.openstreetmap.org/{z}/{x}/{y}.png","type":"url","required":""},domProps:{"value":(_vm.tms_url)},on:{"input":function($event){if($event.target.composing)return;_vm.tms_url=$event.target.value.trim()},"blur":function($event){return _vm.$forceUpdate()}}}),_vm._v(" "),_c('small',[_vm._v(_vm._s(_vm.$t('Enter the TMS service URL (XYZ)')))])]),_vm._v(" "),_c('fieldset',{staticClass:"form-group"},[_c('label',{attrs:{"for":"add_tms_name","title":"required"}},[_c('span',{directives:[{name:"t",rawName:"v-t",value:('Name'),expression:"'Name'"}]}),_vm._v(" "),_c('i',{staticStyle:{"font-family":"Monospace","color":"var(--skin-color)"}},[_vm._v("*")])]),_vm._v(" "),_c('input',{directives:[{name:"model",rawName:"v-model.trim",value:(_vm.tms_name),expression:"tms_name",modifiers:{"trim":true}}],staticClass:"form-control",attrs:{"id":"add_tms_name","type":"text","required":""},domProps:{"value":(_vm.tms_name)},on:{"input":function($event){if($event.target.composing)return;_vm.tms_name=$event.target.value.trim()},"blur":function($event){return _vm.$forceUpdate()}}})]),_vm._v(" "),_c('div',{staticClass:"form-group"},[_c('label',{directives:[{name:"t",rawName:"v-t",value:('Projection'),expression:"'Projection'"}],attrs:{"for":"g3w-tms-projections"}}),_vm._v(" "),_c('select',{directives:[{name:"model",rawName:"v-model",value:(_vm.tms_projection),expression:"tms_projection"}],staticClass:"form-control",attrs:{"id":"g3w-tms-projections"},on:{"change":function($event){var $$selectedVal = Array.prototype.filter.call($event.target.options,function(o){return o.selected}).map(function(o){var val = "_value" in o ? o._value : o.value;return val}); _vm.tms_projection=$event.target.multiple ? $$selectedVal : $$selectedVal[0]}}},[_c('option',{domProps:{"value":'EPSG:3857'}},[_vm._v("EPSG:3857")]),_vm._v(" "),_c('option',{domProps:{"value":'EPSG:4326'}},[_vm._v("EPSG:4326")])])]),_vm._v(" "),_c('div',{staticClass:"form-group"},[_c('label',{directives:[{name:"t",rawName:"v-t",value:('layer_position.message'),expression:"'layer_position.message'"}],attrs:{"for":"position-layer-tms"}}),_vm._v(" "),_c('select',{directives:[{name:"model",rawName:"v-model",value:(_vm.position),expression:"position"}],staticClass:"form-control",attrs:{"id":"position-layer-tms"},on:{"change":function($event){var $$selectedVal = Array.prototype.filter.call($event.target.options,function(o){return o.selected}).map(function(o){var val = "_value" in o ? o._value : o.value;return val}); _vm.position=$event.target.multiple ? $$selectedVal : $$selectedVal[0]}}},[_c('option',{directives:[{name:"t",rawName:"v-t",value:('layer_position.top'),expression:"'layer_position.top'"}],domProps:{"value":'top'}}),_vm._v(" "),_c('option',{directives:[{name:"t",rawName:"v-t",value:('layer_position.bottom'),expression:"'layer_position.bottom'"}],domProps:{"value":'bottom'}})])]),_vm._v(" "),_c('select',{directives:[{name:"model",rawName:"v-model",value:(_vm.tms_visible),expression:"tms_visible"}],attrs:{"id":"g3w-tms-visible","hidden":""},on:{"change":function($event){var $$selectedVal = Array.prototype.filter.call($event.target.options,function(o){return o.selected}).map(function(o){var val = "_value" in o ? o._value : o.value;return val}); _vm.tms_visible=$event.target.multiple ? $$selectedVal : $$selectedVal[0]}}},[_c('option',{domProps:{"value":false}}),_vm._v(" "),_c('option',{domProps:{"value":true}})]),_vm._v(" "),_c('div',{staticClass:"form-group"},[_c('label',{directives:[{name:"t",rawName:"v-t",value:('Opacity'),expression:"'Opacity'"}],attrs:{"for":"g3w-tms-opacity"}}),_vm._v(" "),_c('input',{directives:[{name:"model",rawName:"v-model",value:(_vm.tms_opacity),expression:"tms_opacity"}],attrs:{"id":"g3w-tms-opacity","type":"range","min":"0","max":"1","step":"0.01","list":"tms-opacity-markers"},domProps:{"value":(_vm.tms_opacity)},on:{"__r":function($event){_vm.tms_opacity=$event.target.value}}}),_vm._v(" "),_vm._m(1)])]):_vm._e(),_vm._v(" "),('file' === _vm.layer_type)?_c('div',{staticClass:"form-group"},[(_vm.layer_data)?_c('button',{staticClass:"close",staticStyle:{"float":"right","padding":"5px 10px","margin":"5px 0 0 8px","outline":"1px solid","color":"red","opacity":"1"},attrs:{"type":"button","title":"Remove","data-placement":"left"},on:{"click":_vm.unloadFile}},[_vm._v("×")]):_vm._e(),_vm._v(" "),_c('form',{style:({ padding: _vm.layer_data ? '0' : '20px 0' }),attrs:{"id":"addcustomlayer"}},[_c('input',{ref:"input_file",attrs:{"type":"file","accept":".zip,.geojson,.GEOJSON,.kml,.kmz,.KMZ,.KML,.json,.gpx,.gml,.csv"},on:{"change":_vm.parseFile}}),_vm._v(" "),_c('h4',{staticClass:"skin-color"},[(!_vm.layer_data)?_c('b',{directives:[{name:"t",rawName:"v-t",value:('Add your file here'),expression:"'Add your file here'"}]}):(_vm.layer_name)?_c('b',[_vm._v(_vm._s(_vm.layer_name))]):_vm._e()]),_vm._v(" "),(!_vm.layer_data)?_c('i',{staticClass:"fas fa-cloud-upload-alt fa-5x",attrs:{"aria-hidden":"true"}}):_vm._e(),_vm._v(" "),(!_vm.layer_data)?_c('span',{staticStyle:{"font-family":"Monospace"}},[_vm._v(".gml, .geojson, .kml, .kmz, .gpx, .csv, .zip (shapefile)")]):_vm._e()]),_vm._v(" "),('csv' === _vm.file_type)?_c('div',{staticClass:"form-group",staticStyle:{"padding":"15px","border":"1px solid grey","border-radius":"3px"}},[_c('div',{directives:[{name:"show",rawName:"v-show",value:(_vm.csv_loading),expression:"csv_loading"}],staticClass:"bar-loader"}),_vm._v(" "),_c('label',{directives:[{name:"t",rawName:"v-t",value:('Delimiter'),expression:"'Delimiter'"}],attrs:{"for":"g3w-select-field-layer"}}),_vm._v(" "),_c('select',{directives:[{name:"model",rawName:"v-model",value:(_vm.csv_separator),expression:"csv_separator"}],staticClass:"form-control",attrs:{"id":"g3w-select-separator"},on:{"change":[function($event){var $$selectedVal = Array.prototype.filter.call($event.target.options,function(o){return o.selected}).map(function(o){var val = "_value" in o ? o._value : o.value;return val}); _vm.csv_separator=$event.target.multiple ? $$selectedVal : $$selectedVal[0]},_vm.parseFile]}},[_c('option',[_vm._v(",")]),_vm._v(" "),_c('option',[_vm._v(";")])]),_vm._v(" "),(_vm.fields.length > 1 && !_vm.csv_wkt)?[_c('label',{directives:[{name:"t",rawName:"v-t",value:('X field'),expression:"'X field'"}],attrs:{"for":"g3w-select-x-field"}}),_vm._v(" "),_c('select',{directives:[{name:"model",rawName:"v-model",value:(_vm.csv_x),expression:"csv_x"}],staticClass:"form-control",attrs:{"id":"g3w-select-x-field","disabled":!(_vm.fields || []).length},on:{"change":[function($event){var $$selectedVal = Array.prototype.filter.call($event.target.options,function(o){return o.selected}).map(function(o){var val = "_value" in o ? o._value : o.value;return val}); _vm.csv_x=$event.target.multiple ? $$selectedVal : $$selectedVal[0]},_vm.parseFile]}},_vm._l((_vm.fields),function(h){return _c('option',[_vm._v(_vm._s(h))])}),0),_vm._v(" "),_c('label',{directives:[{name:"t",rawName:"v-t",value:('Y field'),expression:"'Y field'"}],attrs:{"for":"g3w-select-y-field"}}),_vm._v(" "),_c('select',{directives:[{name:"model",rawName:"v-model",value:(_vm.csv_y),expression:"csv_y"}],staticClass:"form-control",attrs:{"id":"g3w-select-y-field","disabled":!(_vm.fields || []).length},on:{"change":[function($event){var $$selectedVal = Array.prototype.filter.call($event.target.options,function(o){return o.selected}).map(function(o){var val = "_value" in o ? o._value : o.value;return val}); _vm.csv_y=$event.target.multiple ? $$selectedVal : $$selectedVal[0]},_vm.parseFile]}},_vm._l((_vm.fields),function(h){return _c('option',[_vm._v(_vm._s(h))])}),0)]:_vm._e(),_vm._v(" "),(_vm.csv_wkt)?[_c('label',{attrs:{"for":"g3w-select-wkt-field"}},[_vm._v("WKT")]),_vm._v(" "),_c('select',{directives:[{name:"model",rawName:"v-model",value:(_vm.csv_wkt),expression:"csv_wkt"}],staticClass:"form-control",attrs:{"id":"g3w-select-y-field"},on:{"change":function($event){var $$selectedVal = Array.prototype.filter.call($event.target.options,function(o){return o.selected}).map(function(o){var val = "_value" in o ? o._value : o.value;return val}); _vm.csv_wkt=$event.target.multiple ? $$selectedVal : $$selectedVal[0]}}},_vm._l((_vm.fields),function(h){return _c('option',[_vm._v(_vm._s(h))])}),0)]:_vm._e(),_vm._v(" "),(0 === _vm.fields.length)?_c('div',{directives:[{name:"t",rawName:"v-t",value:('No valid fields'),expression:"'No valid fields'"}]}):_vm._e(),_vm._v(" "),(_vm.olLayer)?_c('small',{staticStyle:{"color":"red","display":"inline-block","margin-top":"1em"}},[_c('span',{directives:[{name:"t",rawName:"v-t",value:('Features found:'),expression:"'Features found:'"}]}),_vm._v(" "+_vm._s(_vm.feature_count))]):_vm._e()],2):_vm._e(),_vm._v(" "),_c('a',{staticStyle:{"float":"right"},attrs:{"href":`https://epsg.io/${(_vm.layer_crs || '').toLowerCase().replace('epsg:', '')}`,"target":"_blank","title":"Docs","data-placement":"bottom"}},[_c('i',{staticClass:"fa fa-external-link-alt",attrs:{"aria-hidden":"true"}})]),_vm._v(" "),_c('fieldset',{staticClass:"form-group",attrs:{"disabled":_vm.layer_data || ['kml','kmz'].includes(_vm.file_type)}},[_c('label',{directives:[{name:"t",rawName:"v-t",value:('Projection'),expression:"'Projection'"}],attrs:{"for":"projection-layer"}}),_vm._v(" "),_c('select',{directives:[{name:"model",rawName:"v-model",value:(_vm.layer_crs),expression:"layer_crs"}],staticClass:"form-control",attrs:{"id":"projection-layer"},on:{"change":function($event){var $$selectedVal = Array.prototype.filter.call($event.target.options,function(o){return o.selected}).map(function(o){var val = "_value" in o ? o._value : o.value;return val}); _vm.layer_crs=$event.target.multiple ? $$selectedVal : $$selectedVal[0]}}},_vm._l((new Set([_vm.map_crs, 'EPSG:3003','EPSG:3004', 'EPSG:3045', 'EPSG:3857', 'EPSG:4326', 'EPSG:6708', 'EPSG:23032', 'EPSG:23033', 'EPSG:25833', 'EPSG:32632', 'EPSG:32633'])),function(crs){return _c('option',[_vm._v(_vm._s(crs))])}),0)]),_vm._v(" "),(_vm.parse_errors.length)?_c('div',{staticClass:"form-group"},[_c('label',{attrs:{"for":"csv_parse_errors"}},[_vm._v("⚠️ Parse errors:")]),_vm._v(" "),_c('select',{staticClass:"form-control",staticStyle:{"background-color":"gold","font-family":"Monospace"},attrs:{"id":"csv_parse_errors"}},_vm._l((_vm.parse_errors),function({ value, row }){return _c('option',[_vm._v("["+_vm._s(row)+"] "+_vm._s(value))])}),0)]):_vm._e(),_vm._v(" "),(_vm.layer_data)?_c('div',{staticClass:"form-group"},[_c('label',{directives:[{name:"t",rawName:"v-t",value:('layer_position.message'),expression:"'layer_position.message'"}],attrs:{"for":"position-layer"}}),_vm._v(" "),_c('select',{directives:[{name:"model",rawName:"v-model",value:(_vm.position),expression:"position"}],staticClass:"form-control",attrs:{"id":"position-layer"},on:{"change":function($event){var $$selectedVal = Array.prototype.filter.call($event.target.options,function(o){return o.selected}).map(function(o){var val = "_value" in o ? o._value : o.value;return val}); _vm.position=$event.target.multiple ? $$selectedVal : $$selectedVal[0]}}},[_c('option',{directives:[{name:"t",rawName:"v-t",value:('layer_position.top'),expression:"'layer_position.top'"}],domProps:{"value":'top'}}),_vm._v(" "),_c('option',{directives:[{name:"t",rawName:"v-t",value:('layer_position.bottom'),expression:"'layer_position.bottom'"}],domProps:{"value":'bottom'}})])]):_vm._e(),_vm._v(" "),(_vm.layer_data)?_c('div',{staticClass:"form-group"},[_c('label',{directives:[{name:"t",rawName:"v-t",value:('Persistent data'),expression:"'Persistent data'"}],attrs:{"for":"persistent-layer"}}),_vm._v(" "),_c('select',{directives:[{name:"model",rawName:"v-model",value:(_vm.persistent),expression:"persistent"}],staticClass:"form-control",attrs:{"id":"persistent-layer"},on:{"change":function($event){var $$selectedVal = Array.prototype.filter.call($event.target.options,function(o){return o.selected}).map(function(o){var val = "_value" in o ? o._value : o.value;return val}); _vm.persistent=$event.target.multiple ? $$selectedVal : $$selectedVal[0]}}},[_c('option',{directives:[{name:"t",rawName:"v-t",value:('no'),expression:"'no'"}],domProps:{"value":false}}),_vm._v(" "),_c('option',{directives:[{name:"t",rawName:"v-t",value:('yes'),expression:"'yes'"}],domProps:{"value":true}})]),_vm._v(" "),_c('small',{directives:[{name:"t",rawName:"v-t",value:('save layer into browser storage'),expression:"'save layer into browser storage'"}]})]):_vm._e(),_vm._v(" "),((_vm.fields || []).length)?_c('div',{staticClass:"form-group"},[_c('label',{directives:[{name:"t",rawName:"v-t",value:('label'),expression:"'label'"}],attrs:{"for":"g3w-select-field-layer"}}),_vm._v(" "),_c('select',{directives:[{name:"model",rawName:"v-model",value:(_vm.field),expression:"field"}],staticClass:"form-control",attrs:{"id":"g3w-select-field-layer"},on:{"change":function($event){var $$selectedVal = Array.prototype.filter.call($event.target.options,function(o){return o.selected}).map(function(o){var val = "_value" in o ? o._value : o.value;return val}); _vm.field=$event.target.multiple ? $$selectedVal : $$selectedVal[0]}}},[_c('option',{domProps:{"value":null}},[_vm._v("---")]),_vm._v(" "),_vm._l((_vm.fields),function(f){return _c('option',{key:f,domProps:{"value":f}},[_vm._v(_vm._s(f))])})],2),_vm._v(" "),_c('small',{directives:[{name:"t",rawName:"v-t",value:('field shown on map'),expression:"'field shown on map'"}]})]):_vm._e(),_vm._v(" "),(_vm.layer_data)?_c('div',[_c('div',{staticClass:"g3w-color-picker",on:{"click":function($event){$event.stopPropagation();}}},[_c('div',{staticClass:"g3w-color-picker-heading"},[_c('span',{directives:[{name:"t",rawName:"v-t",value:('Color'),expression:"'Color'"}]}),_vm._v(" "),_c('output',[_vm._v(_vm._s(_vm.color.hex))])]),_vm._v(" "),_c('div',{staticClass:"g3w-color-picker-plane",style:({ backgroundColor: _vm.hueColor }),attrs:{"role":"application","tabindex":"0","aria-label":_vm.$t('Color')},on:{"pointerdown":function($event){$event.preventDefault();return _vm.onColorPlanePointerDown.apply(null, arguments)},"pointermove":function($event){$event.preventDefault();return _vm.onColorPlanePointerMove.apply(null, arguments)},"keydown":function($event){$event.stopPropagation();return _vm.onColorPlaneKeydown.apply(null, arguments)}}},[_c('span',{staticClass:"g3w-color-picker-indicator",style:({ left: _vm.saturation * 100 + '%', top: (1 - _vm.brightness) * 100 + '%' })})]),_vm._v(" "),_c('input',{staticClass:"g3w-color-picker-hue",attrs:{"type":"range","min":"0","max":"359","step":"1","aria-label":_vm.$t('Color')},domProps:{"value":_vm.hue},on:{"input":_vm.onColorHueInput}}),_vm._v(" "),_c('label',{staticClass:"g3w-color-picker-label"},[_c('span',{directives:[{name:"t",rawName:"v-t",value:('Opacity'),expression:"'Opacity'"}]}),_vm._v(" "),_c('output',[_vm._v(_vm._s(Math.round(_vm.alpha * 100))+"%")]),_vm._v(" "),_c('input',{staticClass:"g3w-color-picker-opacity",attrs:{"type":"range","min":"0","max":"1","step":"0.01","aria-label":_vm.$t('Opacity')},domProps:{"value":_vm.alpha},on:{"input":_vm.onColorOpacityInput}})])])]):_vm._e()]):_vm._e()]),_vm._v(" "),_c('menu',{staticStyle:{"text-align":"right","border-top":"1px solid #f4f4f4"}},[(_vm.error_message)?_c('div',{directives:[{name:"t",rawName:"v-t",value:(_vm.error_message),expression:"error_message"}],staticStyle:{"font-weight":"bold","font-size":"1.2em","background-color":"orange","padding":"10px","text-align":"center","margin-bottom":"5px"}}):_vm._e(),_vm._v(" "),_c('button',{directives:[{name:"t",rawName:"v-t",value:('close'),expression:"'close'"}],staticClass:"btn btn-default",staticStyle:{"font-weight":"bold","min-width":"70px"},attrs:{"value":"cancel","formnovalidate":""}}),_vm._v(" "),_c('button',{directives:[{name:"t",rawName:"v-t",value:('add'),expression:"'add'"}],staticClass:"btn btn-success",staticStyle:{"font-weight":"bold","min-width":"70px","margin-bottom":"0","margin-left":"5px"},attrs:{"type":"button","disabled":!_vm.can_add_layer},on:{"click":function($event){$event.stopPropagation();return _vm.addLayer.apply(null, arguments)}}})])])])
}
var staticRenderFns = [function (){var _vm=this,_c=_vm._self._c;return _c('datalist',{staticStyle:{"display":"flex","justify-content":"space-between"},attrs:{"id":"wms-opacity-markers"}},[_c('option',{attrs:{"value":"0"}},[_vm._v("0")]),_vm._v(" "),_c('option',{attrs:{"value":"0.25"}},[_vm._v("0.25")]),_vm._v(" "),_c('option',{attrs:{"value":"0.50"}},[_vm._v("0.50")]),_vm._v(" "),_c('option',{attrs:{"value":"0.75"}},[_vm._v("0.75")]),_vm._v(" "),_c('option',{attrs:{"value":"1"}},[_vm._v("1")])])
},function (){var _vm=this,_c=_vm._self._c;return _c('datalist',{staticStyle:{"display":"flex","justify-content":"space-between"},attrs:{"id":"tms-opacity-markers"}},[_c('option',{attrs:{"value":"0"}},[_vm._v("0")]),_vm._v(" "),_c('option',{attrs:{"value":"0.25"}},[_vm._v("0.25")]),_vm._v(" "),_c('option',{attrs:{"value":"0.50"}},[_vm._v("0.50")]),_vm._v(" "),_c('option',{attrs:{"value":"0.75"}},[_vm._v("0.75")]),_vm._v(" "),_c('option',{attrs:{"value":"1"}},[_vm._v("1")])])
}]

const __vue_render__ = render;
const __vue_staticRenderFns__ = staticRenderFns;

const __vue_component__ = __vue_script__;
__vue_component__.render = __vue_render__;
__vue_component__.staticRenderFns = __vue_staticRenderFns__;
__vue_component__._compiled = true;
__vue_component__.__file = "/home/volterra79/G3W-SUITE/g3w-client/src/components/ModalAddLayer.vue";
__vue_component__._scopeId = "data-v-b1524fe0";
/* style inject */
import __vue_create_injector__ from 'g3w-vue:style-injector';
[{"id":"b1524fe0-0","css":".g3w-color-picker[data-v-b1524fe0]{display:grid;gap:8px;width:100%;color:#263238}.g3w-color-picker-heading[data-v-b1524fe0]{display:flex;align-items:center;justify-content:space-between;gap:8px;font-weight:600}.g3w-color-picker-label[data-v-b1524fe0]{display:grid;grid-template-columns:1fr auto;align-items:center;gap:6px 8px;margin:0;font-weight:600}.g3w-color-picker output[data-v-b1524fe0]{color:#53636b;font:12px monospace}.g3w-color-picker-plane[data-v-b1524fe0]{position:relative;aspect-ratio:2 / 1;overflow:hidden;border-radius:3px;cursor:crosshair;touch-action:none;background-image:linear-gradient(to top,#000,transparent),linear-gradient(to right,#fff,transparent)}.g3w-color-picker-plane[data-v-b1524fe0]:focus-visible{outline:2px solid var(--skin-focus);outline-offset:2px}.g3w-color-picker-indicator[data-v-b1524fe0]{position:absolute;width:14px;height:14px;border:2px solid #fff;border-radius:50%;box-shadow:0 0 0 1px #1e292e,0 1px 3px #0008;transform:translate(-50%,-50%);pointer-events:none}.g3w-color-picker input[type=range][data-v-b1524fe0]{grid-column:1 / -1;width:100%;margin:0;cursor:pointer}.g3w-color-picker-hue[data-v-b1524fe0]{height:14px;border-radius:7px;appearance:none;background:linear-gradient(to right,red,#ff0,#0f0,#0ff,#00f,#f0f,red)}.g3w-color-picker-hue[data-v-b1524fe0]::-webkit-slider-thumb{width:18px;height:18px;border:2px solid #fff;border-radius:50%;appearance:none;background:#fff;box-shadow:0 1px 4px #0008}.g3w-color-picker-hue[data-v-b1524fe0]::-moz-range-thumb{width:14px;height:14px;border:2px solid #fff;border-radius:50%;background:#fff;box-shadow:0 1px 4px #0008}.g3w-color-picker-opacity[data-v-b1524fe0]{accent-color:var(--skin-color)}#addcustomlayer[data-v-b1524fe0]{margin:10px 0;position:relative;border:2px dashed #97A1A8;display:flex;flex-direction:column;text-align:center;gap:8px;opacity:.8}#addcustomlayer input[data-v-b1524fe0]{position:absolute;outline:none;opacity:0;cursor:pointer;inset:0}#add_wms_url[data-v-b1524fe0]::placeholder{font-size:85%;opacity:.5}.g3w-wms-panel-title[data-v-b1524fe0]{font-size:1.2em;font-weight:700;margin-bottom:10px}\n","media":"","filename":"src/components/ModalAddLayer.vue"}].forEach(__vue_create_injector__());
export default __vue_component__;